/**
 * Hook for service worker registration and lifecycle management
 */

import { useEffect, useState } from 'react'

export interface ServiceWorkerState {
  registration: ServiceWorkerRegistration | null
  updateAvailable: boolean
  isUpdatePending: boolean
  error: Error | null
}

export function useServiceWorker() {
  const [state, setState] = useState<ServiceWorkerState>({
    registration: null,
    updateAvailable: false,
    isUpdatePending: false,
    error: null,
  })

  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return
    }

    let registration: ServiceWorkerRegistration | null = null

    const registerServiceWorker = async () => {
      try {
        registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
        })

        setState((prev) => ({
          ...prev,
          registration,
          error: null,
        }))

        // Check for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration?.installing
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setState((prev) => ({
                  ...prev,
                  updateAvailable: true,
                  isUpdatePending: true,
                }))
              }
            })
          }
        })

        // Listen for controller change (update activated)
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          setState((prev) => ({
            ...prev,
            updateAvailable: false,
            isUpdatePending: false,
          }))
        })
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error: error instanceof Error ? error : new Error('Service worker registration failed'),
        }))
        console.error('Service Worker registration failed:', error)
      }
    }

    registerServiceWorker()

    // Cleanup
    return () => {
      // Service worker registration persists, no cleanup needed
    }
  }, [])

  return state
}

